/**
 * @fileoverview SSE Event Handler Hook
 * 
 * Handles incoming SSE events and translates them into state updates.
 * Single responsibility: event parsing and state mutation via callbacks.
 */

import { useCallback } from 'react';
import { optimizationEventService } from '../../services/optimization/OptimizationEventService';
import { optimizationDebugLog } from '../../utils/optimizationDebugLogger';
import type { OptimizationEvent, OptimizationJob } from './types';

export interface JobEventCallbacks {
  /** Called when job progress updates */
  onProgress: (jobId: string, progress: number, current: number, total: number, estimatedTimeRemaining?: number) => void;
  /** Called when job completes successfully */
  onCompleted: (jobId: string, resultData: Partial<OptimizationJob>) => void;
  /** Called when job fails */
  onFailed: (jobId: string, error: string) => void;
  /** Called when a fetch should be triggered after completion */
  onShouldFetch: () => void;
}

export interface UseJobEventHandlerReturn {
  /** Handle an incoming SSE event */
  handleEvent: (event: OptimizationEvent) => void;
  /** Subscribe to events for a specific job */
  subscribeToJob: (jobId: string) => () => void;
  /** Subscribe to global events */
  subscribeGlobal: () => () => void;
}

/**
 * Parse best result from event data
 */
function extractBestResult(eventData: any, previousBestResult?: any) {
  const rawBestResult = eventData.bestResult || eventData.bestSoFar;
  
  if (!rawBestResult) {
    return previousBestResult;
  }

  return {
    parameters: rawBestResult.parameters || previousBestResult?.parameters || {},
    score: rawBestResult.score ?? previousBestResult?.score ?? 0,
    metrics: rawBestResult.metrics || previousBestResult?.metrics,
    trades: rawBestResult.trades || previousBestResult?.trades,
    signals: rawBestResult.signals || previousBestResult?.signals,
    equity: rawBestResult.equity || previousBestResult?.equity,
  };
}

/**
 * Extract completion data from event
 */
function extractCompletionData(eventData: any): Partial<OptimizationJob> {
  const bestResult = eventData.bestResult;
  const allResults = Array.isArray(eventData.allResults) ? eventData.allResults : undefined;
  const parameterRanges = eventData.parameterRanges || undefined;
  const optimizationTarget = eventData.optimizationTarget || undefined;

  optimizationDebugLog('useJobEventHandler', 'Extracting completion data', {
    hasBestResult: !!bestResult,
    hasAllResults: !!allResults,
    allResultsCount: allResults?.length || 0,
    hasParameterRanges: !!parameterRanges,
    hasOptimizationTarget: !!optimizationTarget,
    hasStrategy: !!eventData.strategy,
  }, { console: false });

  return {
    bestResult,
    allResults,
    parameterRanges,
    optimizationTarget,
    currentCombination: eventData.totalCombinations || 0,
    totalCombinations: eventData.totalCombinations || 0,
    parameters: eventData.config,
    // Preserve strategy for job matching
    strategy: eventData.strategy || eventData.config?.strategy,
    symbol: eventData.symbol || eventData.config?.symbol,
    interval: eventData.interval || eventData.config?.interval,
  };
}

/**
 * Hook for handling SSE job events
 */
export function useJobEventHandler(callbacks: JobEventCallbacks): UseJobEventHandlerReturn {
  const { onProgress, onCompleted, onFailed, onShouldFetch } = callbacks;

  const handleEvent = useCallback((event: OptimizationEvent) => {
    const { type, jobId, data } = event;

    if (type === 'progress' && 'current' in data) {
      const progressValue = data.progress || 0;
      const currentValue = data.current;
      const totalValue = data.total;
      const estimatedTimeRemaining = data.estimatedTimeRemaining;

      onProgress(jobId, progressValue, currentValue, totalValue, estimatedTimeRemaining);

      // Check for implicit completion via progress
      const isMarkedComplete = 'isComplete' in data && !!(data as any).isComplete;
      const isFinalProgress = progressValue >= 100 || (totalValue > 0 && currentValue >= totalValue);

      if (isMarkedComplete || isFinalProgress) {
        optimizationDebugLog('useJobEventHandler', 'Progress reached completion threshold', {
          jobId,
          progress: progressValue,
          current: currentValue,
          total: totalValue,
        }, { console: false });

        const resultData = extractCompletionData(data);
        onCompleted(jobId, resultData);
        onShouldFetch();
      }
    } else if (type === 'completed') {
      optimizationDebugLog('useJobEventHandler', 'Handling completion event', {
        jobId,
        eventDataKeys: Object.keys(data),
      }, { console: false });

      const resultData = extractCompletionData(data);
      onCompleted(jobId, resultData);
      onShouldFetch();
    } else if (type === 'failed' && 'error' in data) {
      onFailed(jobId, data.error);
    }
  }, [onProgress, onCompleted, onFailed, onShouldFetch]);

  const subscribeToJob = useCallback((jobId: string) => {
    return optimizationEventService.subscribe(jobId, handleEvent);
  }, [handleEvent]);

  const subscribeGlobal = useCallback(() => {
    return optimizationEventService.subscribeGlobal(handleEvent);
  }, [handleEvent]);

  return {
    handleEvent,
    subscribeToJob,
    subscribeGlobal,
  };
}
